import type { CSSProperties, ReactNode } from 'react';
import {
  type ColorPalette,
  FONT_SIZE,
  FONT_WEIGHT,
  Icons,
  LINE_HEIGHT,
  RADIUS,
  SIZE,
  SPACE,
} from '@vigilo/design-system';

export const CROWD_PAGINATION_DEFAULT_PAGE_SIZE = 25;
export const CROWD_PAGINATION_MAX_PAGE_SIZE = 100;
export const CROWD_PAGINATION_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export interface CrowdPaginationEnvelope {
  total: number;
  page: number;
  pageSize: number;
  hasNextPage: boolean;
}

export interface PaginationControlsProps {
  C: ColorPalette;
  pagination: CrowdPaginationEnvelope | null;
  loading?: boolean;
  error?: string | null;
  emptyLabel?: string;
  pageSizeOptions?: readonly number[];
  disabled?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export function PaginationControls({
  C,
  pagination,
  loading = false,
  error = null,
  emptyLabel = 'No rows',
  pageSizeOptions = CROWD_PAGINATION_PAGE_SIZE_OPTIONS,
  disabled = false,
  onPageChange,
  onPageSizeChange,
}: PaginationControlsProps) {
  const page = clampPositiveInt(pagination?.page, 1);
  const pageSize = clampPositiveInt(pagination?.pageSize, CROWD_PAGINATION_DEFAULT_PAGE_SIZE);
  const total = Math.max(0, Math.trunc(Number.isFinite(pagination?.total) ? pagination?.total ?? 0 : 0));
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : ((page - 1) * pageSize) + 1;
  const end = Math.min(total, page * pageSize);
  const locked = disabled || loading || Boolean(error) || !pagination;
  const canGoPrevious = !locked && page > 1;
  const canGoNext = !locked && pagination.hasNextPage;
  const statusText = getStatusText({ loading, error, total, start, end, emptyLabel });
  const options = normalizePageSizeOptions(pageSizeOptions, pageSize);

  return (
    <div style={containerStyle(C)} data-crowd-pagination-envelope="data-total-page-pageSize-hasNextPage">
      <div style={statusStyle(C)} aria-live={loading ? 'polite' : 'off'}>
        {statusText}
      </div>

      <div style={controlsStyle}>
        <label style={pageSizeLabelStyle(C)}>
          Rows
          <select
            aria-label="Rows per page"
            value={pageSize}
            disabled={locked}
            onChange={event => onPageSizeChange(Number(event.target.value))}
            style={selectStyle(C)}
          >
            {options.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <div style={pagerStyle(C)}>
          <IconPageButton
            C={C}
            label="Previous page"
            disabled={!canGoPrevious}
            onClick={() => onPageChange(page - 1)}
          >
            <Icons.ChevL size={SIZE.iconXs} />
          </IconPageButton>

          <span style={pageLabelStyle(C)}>
            Page {page} of {pageCount}
          </span>

          <IconPageButton
            C={C}
            label="Next page"
            disabled={!canGoNext}
            onClick={() => onPageChange(page + 1)}
          >
            <Icons.ChevR size={SIZE.iconXs} />
          </IconPageButton>
        </div>
      </div>
    </div>
  );
}

interface IconPageButtonProps {
  C: ColorPalette;
  label: string;
  disabled: boolean;
  children: ReactNode;
  onClick: () => void;
}

function IconPageButton({ C, label, disabled, children, onClick }: IconPageButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      style={iconButtonStyle(C, disabled)}
    >
      {children}
    </button>
  );
}

function getStatusText({
  loading,
  error,
  total,
  start,
  end,
  emptyLabel,
}: {
  loading: boolean;
  error: string | null;
  total: number;
  start: number;
  end: number;
  emptyLabel: string;
}): string {
  if (loading) return 'Loading...';
  if (error) return error;
  if (total === 0) return emptyLabel;
  return `Showing ${start}-${end} of ${total}`;
}

function normalizePageSizeOptions(options: readonly number[], currentPageSize: number): number[] {
  return Array.from(new Set([...options, currentPageSize]))
    .map(option => clampPositiveInt(option, CROWD_PAGINATION_DEFAULT_PAGE_SIZE))
    .filter(option => option <= CROWD_PAGINATION_MAX_PAGE_SIZE)
    .sort((a, b) => a - b);
}

function clampPositiveInt(value: unknown, fallback: number): number {
  const parsed = typeof value === 'number' ? value : NaN;
  return Number.isFinite(parsed) && parsed > 0
    ? Math.trunc(parsed)
    : fallback;
}

function containerStyle(C: ColorPalette): CSSProperties {
  return {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) max-content',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: SPACE[8],
    minHeight: 38,
    width: '100%',
    borderTop: `1px solid ${C.border2}`,
    padding: `${SPACE[8]}px ${SPACE[10]}px`,
    color: C.sub,
    fontFamily: "'Albert Sans', sans-serif",
  };
}

function statusStyle(C: ColorPalette): CSSProperties {
  return {
    minWidth: 0,
    color: C.sub,
    fontSize: FONT_SIZE.body,
    fontWeight: FONT_WEIGHT.medium,
    lineHeight: LINE_HEIGHT.pill,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
}

const controlsStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: SPACE[6],
  flex: '0 0 auto',
  flexWrap: 'nowrap',
  minWidth: 'max-content',
  whiteSpace: 'nowrap',
};

function pageSizeLabelStyle(C: ColorPalette): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: SPACE[6],
    color: C.muted,
    fontSize: FONT_SIZE.base,
    fontWeight: FONT_WEIGHT.bold,
    lineHeight: LINE_HEIGHT.tight,
  };
}

function selectStyle(C: ColorPalette): CSSProperties {
  return {
    height: 28,
    minWidth: 60,
    borderRadius: RADIUS.md,
    border: `1px solid ${C.border2}`,
    background: C.surface,
    color: C.text,
    padding: `0 ${SPACE[7]}px`,
    fontFamily: "'Albert Sans', sans-serif",
    fontSize: FONT_SIZE.body,
    fontWeight: FONT_WEIGHT.semibold,
    outline: 'none',
  };
}

function pagerStyle(C: ColorPalette): CSSProperties {
  return {
    display: 'inline-grid',
    gridTemplateColumns: '28px minmax(82px, auto) 28px',
    alignItems: 'center',
    gap: SPACE[6],
    color: C.sub,
  };
}

function pageLabelStyle(C: ColorPalette): CSSProperties {
  return {
    color: C.sub,
    fontSize: FONT_SIZE.body,
    fontWeight: FONT_WEIGHT.semibold,
    lineHeight: LINE_HEIGHT.tight,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  };
}

function iconButtonStyle(C: ColorPalette, disabled: boolean): CSSProperties {
  return {
    width: 28,
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: RADIUS.card,
    border: `1px solid ${C.border2}`,
    background: disabled ? C.faint : 'transparent',
    color: C.text,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    padding: 0,
  };
}
